import { Avatar, Box, TextField, Typography } from '@mui/material'
import React, { useRef, useState } from 'react'
import { Buyer } from '../../../../Models/Buyer'
import { deepOrange } from '@mui/material/colors'
import "./BuyerProfileEditForm.scss";
import SnackBar, { SnackTypes } from '../../../UtilityComponents/SnackBar/SnackBar';

import GGLoaderButton from '../../../CommonComponents/GGLoaderButton/GGLoaderButton';
import GGButton from '../../../CommonComponents/GGButton/GGButton';
import { Edit } from '@mui/icons-material';
import VerifiedWidget from '../../../UtilityComponents/VerifiedWidget/VerifiedWidget';
import GGDialogueBox, { dialogueType } from '../../../UtilityComponents/GGDialogueBox/GGDialogueBox';
import { BuyerAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/BuyerAPIHandler';
import Delete from '@mui/icons-material/Delete';
import { compressImage } from '../../../../utils/FileCompressionUtils';
import { getUploadErrorMessage, ResumableUploadTask } from '../../../../services/UploadClient/ResumableUploadTask';
import { UploadPurpose } from '../../../../services/NetworkClient/BackendAPIs/UploadAPIHandler';

interface BuyerProfileEditFormProps {
    buyer: Buyer
    exitEditPage: (buyer: Buyer, didUpdate: boolean, wasUpdateSuccess: boolean) => void
}
const BuyerProfileEditForm = ({ buyer, exitEditPage }: BuyerProfileEditFormProps) => {
    const [editedBuyerDetails, setEditedBuyerDetails] = useState<Buyer>(buyer);
    const [uploadedProfileImage, setUploadedProfileImage] = useState<File | undefined>(undefined)
    const [loading, setLoading] = useState(false);
    const [discardChangedDialogueOpen, setDiscardChangedDialogueOpen] = useState(false);
    // Snackbar based status handling 
    const snackBarRef = useRef<{ showSnackbar(): any }>(null)
    const [snackBarParams, setSnackBarParams] = useState({
        message: "",
        state: SnackTypes.error
    });

    const showSnackbar = (message: string, state: SnackTypes) => {
        setSnackBarParams({ message: message, state: state })
        snackBarRef.current?.showSnackbar()
    }
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);

    const handleBuyerData = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const fieldName = e.target.name;
        const val = e.target.value;
        setEditedBuyerDetails({ ...editedBuyerDetails, [fieldName]: val });
    };

    const handleProfilePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setUploadedProfileImage(file)
            setEditedBuyerDetails((prev) => ({
                ...prev,
                profileImage: objectUrl, // Update the profile image preview
            }));
        }
    };

    const triggerFileUpload = () => {
        fileInputRef.current?.click();
    };

    const saveButtonClicked = async () => {
        setLoading(true)
        try {
            // Handle profile image upload here as well.
            await handleProfileImageChange()
            const newBuyer = await BuyerAPIHandler.shared.updateBuyer(editedBuyerDetails);
            showSnackbar("Details updated successfully", SnackTypes.success);
            exitEditPage(newBuyer, true, true);
        } catch (err) {
            showSnackbar(getUploadErrorMessage(err), SnackTypes.error)
        }
        setLoading(false)
    }

    const handleProfileImageChange = async () => {
        // Check if any change has been done for profile image
        if (buyer.profileImage === editedBuyerDetails.profileImage) return;

        // Delete the existing profile photo
        if (buyer.profileImage && buyer.profileImage !== "")
            await handleFileDeletion(buyer.profileImage)

        // Check if a new upload is being made and delete the existing image
        if (uploadedProfileImage){

            // compress image
            const compressedImage = await compressImage(uploadedProfileImage, 0.05);

            // upload image
            await handleFileUpload(compressedImage);
        }
    }

    const handleFileUpload = async (file: File) => {
        const asset = await new ResumableUploadTask(file, {
            purpose: UploadPurpose.BUYER_PROFILE_PHOTO,
        }).start();
        editedBuyerDetails.profileImageAssetId = asset.assetId;
        editedBuyerDetails.profileImage = asset.url || "";
    }

    const handleFileDeletion = async (_fileUrl: string) => {
        editedBuyerDetails.profileImage = ""
        editedBuyerDetails.profileImageAssetId = undefined;
    }

    return (
        <div className='client-profile-edit-section'>
            <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={5000} message={snackBarParams.message} />

            <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleProfilePhotoUpload}
            />

            <div className="info-section">
                <div className="left-panel">
                    <TextField
                        name="fullName"
                        label="Name"
                        value={editedBuyerDetails.fullName}
                        onChange={handleBuyerData}
                        variant="outlined"
                        helperText={
                            <Box className="helper-container">
                                <Box>{editedBuyerDetails.fullName.length}/30</Box>
                            </Box>
                        }
                        inputProps={{ maxLength: 30 }}
                        sx={{ marginBottom: '20px' }}
                        fullWidth
                    />
                    <TextField
                        name="about"
                        label="About Me (optional)"
                        placeholder='Tell us something about yourself'
                        value={editedBuyerDetails.about}
                        onChange={handleBuyerData}
                        variant="outlined"
                        helperText={
                            <Box className="helper-container">
                                <Box>{editedBuyerDetails.about?.length ?? 0}/200</Box>
                            </Box>
                        }
                        multiline
                        rows={2}
                        inputProps={{ maxLength: 200 }}
                        sx={{ marginBottom: '10px' }}
                        fullWidth
                    />
                </div>

                <div className="right-panel">
                    <div id='avatar-container'>
                        <Avatar
                            id='avatar'
                            alt={editedBuyerDetails.fullName}
                            src={editedBuyerDetails.profileImage}
                            sx={{ width: 150, height: 150, cursor: "pointer" }}
                            onClick={triggerFileUpload}
                        > 
                            <Box sx={{height:"100%", width: "100%", bgcolor: deepOrange[500]}} className="center">
                                <Typography fontSize={'75px'}> {editedBuyerDetails.fullName[0]} </Typography>
                            </Box>
                        </Avatar>

                        {/* Hovering Delete Button */}
                        {editedBuyerDetails.profileImage && (
                            <Delete
                                id="delete-profile-image-icon"
                                fontSize="small"
                                onClick={() => {
                                    setUploadedProfileImage(undefined);
                                    setEditedBuyerDetails((prev) => ({
                                        ...prev,
                                        profileImage: "", // Update the profile image preview
                                    }))
                                }
                                }
                            />
                        )}
                    </div>

                    <GGButton id='avatar-change-btn' variant='text' startIcon={<Edit />} onClick={triggerFileUpload}>
                        Upload new picture
                    </GGButton>
                </div>
            </div>

            <div className="separator"></div>
            <div className="contact-section">
                <Typography variant='h6' fontWeight={1000} marginBottom={'10px'}> Contact Information </Typography>
                <div className='contact-row'>
                    <TextField
                        name="mobileNo"
                        label="Mobile Number"
                        value={editedBuyerDetails.mobileNo}
                        onChange={(e) => {
                            e.target.value = e.target.value.replace(/[^\d]+/, '');
                            handleBuyerData(e);
                        }}
                        InputProps={{ style: { fontFamily: "Arial" } }}
                        variant="outlined"
                        inputProps={{ maxLength: 10 }}
                        fullWidth
                    />
                    <div className='verified-section'>
                        <VerifiedWidget verified={editedBuyerDetails.mobileNoVerified} />
                    </div>
                </div>
                <div className='contact-row'>
                    <TextField
                        name="email"
                        label="Email"
                        id='email'
                        value={editedBuyerDetails.email}
                        onChange={handleBuyerData}
                        variant="outlined"
                        fullWidth
                        disabled
                    />
                    <div className='verified-section'>
                        <VerifiedWidget verified={true} />
                    </div>
                </div>
            </div>

            <div className="button-section">
                <GGButton id='discard-button' onClick={() => setDiscardChangedDialogueOpen(true)}>Discard</GGButton>
                <GGLoaderButton id='save-button' isLoading={loading} colorHex='#000000' onClick={saveButtonClicked}>
                    Save
                </GGLoaderButton>
            </div>

            <GGDialogueBox
                type={dialogueType.Alert}
                isOpen={discardChangedDialogueOpen}
                message={'You will lose all the unsaved changes you have made so far.'}
                successHandler={() => {
                    setDiscardChangedDialogueOpen(false)
                    if (exitEditPage)
                        exitEditPage(buyer, false, false)
                }}
                closeHandler={() => setDiscardChangedDialogueOpen(false)} />
        </div>
    );
};

export default BuyerProfileEditForm;
